import {assert} from 'Asserts';
import {pluralize, navigate} from 'BenchlingUtil';
import {Section} from 'constants/NavConstants';
import doesEvaluatedTableHaveAnyErrors from 'notebook/tables/errors/doesEvaluatedTableHaveAnyErrors';
import notifications from 'notifications';
import * as ProcessManagementDAO from 'processes/dao/ProcessManagementDAO';
import {RouteToEndNodesParams} from 'processes/dao/ProcessManagementDAO';
import {
  getOutputsFilteredByHumanIds,
  getTasksFilteredByHumanIds,
} from 'processes/dashboard/getModelFilteredByProcessesrUrl';
import {getStore} from 'redux-stores/Store';
import {forceRefreshSearch} from 'search/SearchUIReduxActions';
import ModalButtonKey from 'test/lib/cypress/shared/ModalButtonKey';
import {ButtonStyle} from 'ui-library/buttons/Button';
import {ModalButton} from 'ui-library/modals/Modal';
import {withLoadingScreen} from 'util/Promises';
import {assertNever} from 'util/Types';

import {buildFixFailedMappingSpecs} from './buildFixFailedMappingSpecs';
import {
  ProcessTask,
  ProcessAsyncRecord,
  FromItemIdsToTables,
  FixFailedRoutingAndMappingStepKey,
  isAsyncDataStartAutomatedMapping,
  isAsyncRecordRoutingRecord,
} from './FixFailedRoutingAndMappingConstants';
import {trackAnalytics} from './FixFailedRoutingAndMappingUtils';

interface GetFixFailedModalButtonsArgs {
  currentPane: FixFailedRoutingAndMappingStepKey;
  selectedItem: ProcessTask | null | undefined;
  selectedRecord: ProcessAsyncRecord | null;
  itemIdsToMapperId: {[id: string]: Array<string>};
  itemIdsToEndNodeId: {[id: string]: Array<string>};
  fromItemIdsToTables: FromItemIdsToTables | null;
  resetFlow: () => void;
  setCurrentPane: (pane: FixFailedRoutingAndMappingStepKey) => void;
  onRequestClose: () => void;
  hasBlockedExpressions: (asyncRecord: ProcessAsyncRecord) => boolean;
}

export function getFixFailedModalButtons(args: GetFixFailedModalButtonsArgs): Array<ModalButton> {
  const {
    currentPane,
    selectedItem,
    selectedRecord,
    itemIdsToMapperId,
    itemIdsToEndNodeId,
    fromItemIdsToTables,
    resetFlow,
    setCurrentPane,
    onRequestClose,
    hasBlockedExpressions,
  } = args;

  const submitButton = {
    key: ModalButtonKey.SUBMIT,
    text: 'Submit',
    dataTestAttrs: {
      component: 'FixFailedRoutingAndMappingModal',
      element: 'submitButton',
      key: ModalButtonKey.SUBMIT,
    },
    style: ButtonStyle.PRIMARY,
    onClick: async () => {
      const rootTask = selectedItem!.rootTask ?? selectedItem!;
      const {taskSpecs, outputSpecs} =
        fromItemIdsToTables != null
          ? buildFixFailedMappingSpecs(fromItemIdsToTables, rootTask.id, rootTask.process.id)
          : {taskSpecs: [], outputSpecs: []};

      const endNodeSpecs = selectedRecord
        ? Object.entries(itemIdsToEndNodeId)
            .map(([_, endNodeIds]) =>
              endNodeIds.length > 0
                ? {
                    endNodeIds,
                    asyncJobRecordId: selectedRecord.id,
                  }
                : null
            )
            .filter((endNodeSpec): endNodeSpec is RouteToEndNodesParams => endNodeSpec != null)
        : [];

      const itemsBeingCreated = [
        ...(taskSpecs.length > 0 ? [pluralize(taskSpecs.length, 'task')] : []),
        ...(outputSpecs.length > 0 ? [pluralize(outputSpecs.length, 'output')] : []),
      ].join(' and ');
      const response = await withLoadingScreen(
        ProcessManagementDAO.fixFailedRoutingAndMapping({
          tasks: taskSpecs,
          outputs: outputSpecs,
          taskId: selectedItem!.id,
          endNodes: endNodeSpecs,
        }),
        itemsBeingCreated.length > 0 ? `Creating new ${itemsBeingCreated}` : 'Performing routing'
      );
      if (taskSpecs.length > 0) {
        navigate(null, getTasksFilteredByHumanIds(response.createdTasks.map((task) => task.humanId)));
      } else if (outputSpecs.length > 0) {
        navigate(null, getOutputsFilteredByHumanIds(response.createdOutputs.map((output) => output.humanId)));
      } else {
        navigate(null, getTasksFilteredByHumanIds([response.routedTask.humanId]));
      }
      // The listing we just navigated to is a read-after-write on the items we just created. Global listing
      // searches default to eventual consistency, so force a consistent (index-refreshed) refresh of the
      // processes listing to guarantee the newly created items are immediately visible. Deferred so it runs
      // after the navigation above has updated the processes search session's params.
      setTimeout(() => {
        getStore().dispatch(forceRefreshSearch(Section.PROCESSES));
      }, 0);
      notifications.success(
        itemsBeingCreated.length > 0 ? `Created new ${itemsBeingCreated}.` : 'Routing complete.'
      );

      trackAnalytics(selectedItem!, Boolean(selectedRecord && isAsyncRecordRoutingRecord(selectedRecord)));
      onRequestClose();
    },
    disabled:
      fromItemIdsToTables == null ||
      Object.values(fromItemIdsToTables).some((mapperDatas) =>
        mapperDatas.some(({computedTable}) => doesEvaluatedTableHaveAnyErrors(computedTable))
      ),
  };

  switch (currentPane) {
    case FixFailedRoutingAndMappingStepKey.SELECT_ERRORS:
      return [];
    case FixFailedRoutingAndMappingStepKey.SELECT_DOWNSTREAM_NODES:
      return [
        {
          key: ModalButtonKey.BACK,
          text: 'Back',
          dataTestAttrs: {
            component: 'FixFailedRoutingAndMappingModal',
            element: 'backButton',
            key: ModalButtonKey.BACK,
          },
          onClick: () => {
            resetFlow();
          },
        },
        {
          key: ModalButtonKey.NEXT,
          text: 'Next',
          dataTestAttrs: {
            component: 'FixFailedRoutingAndMappingModal',
            element: 'nextButton',
            key: ModalButtonKey.NEXT,
          },
          style: ButtonStyle.PRIMARY,
          onClick: () => {
            setCurrentPane(FixFailedRoutingAndMappingStepKey.TABLE_REVIEW);
          },
          disabled: Object.entries(itemIdsToMapperId).some(
            ([itemId, mapperIds]) => mapperIds.length === 0 && itemIdsToEndNodeId[itemId].length === 0
          ),
        },
      ];

    case FixFailedRoutingAndMappingStepKey.TABLE_REVIEW:
      return [
        {
          key: ModalButtonKey.BACK,
          text: 'Back',
          dataTestAttrs: {
            component: 'FixFailedRoutingAndMappingModal',
            element: 'backButton',
            key: ModalButtonKey.BACK,
          },
          onClick: () => {
            if (
              selectedRecord != null &&
              isAsyncDataStartAutomatedMapping(selectedRecord.asyncData) &&
              hasBlockedExpressions(selectedRecord)
            ) {
              setCurrentPane(FixFailedRoutingAndMappingStepKey.SELECT_DOWNSTREAM_NODES);
            } else {
              resetFlow();
            }
          },
        },
        submitButton,
      ];
    default:
      assert(false, `Unexpected type ${assertNever(currentPane)}`);
      return [];
  }
}
